/**
 * The scrolling part of the show sheet, and the writes that come out of it.
 *
 * Split from the sheet itself, which owns the frame: the header, the dismiss
 * gesture and which drawers are open.
 */
import { fromEpisode, inSeason, seasonState, throughEpisode, throughSeason } from "@/lib/seasons";
import type { SeasonEpisode, SeasonView } from "@/lib/seasons";
import { SeasonList } from "@/screens/Show/SeasonList";
import type { OutboxAction } from "@/store/outbox";

/** The three write paths out of the sheet, kept together and out of the view. */
export function buildActions(
  simklId: number | null,
  seasons: readonly SeasonView[],
  onAction: (action: OutboxAction) => void,
) {
  return {
    onToggleEpisode: (episode: SeasonEpisode) => {
      if (simklId === null) return;
      const target = { simklId, season: episode.season, episode: episode.episode };
      onAction({ kind: episode.watched ? "unwatch" : "watch", target });
    },
    // The row, unlike the dot, means "I got this far" and sweeps everything
    // before it. That is the action the 46 import-stranded shows need.
    onSetPosition: (episode: SeasonEpisode) => {
      if (simklId === null) return;
      const targets = episode.watched
        ? fromEpisode(seasons, simklId, episode)
        : throughEpisode(seasons, simklId, episode);
      if (targets.length === 0) return;
      onAction({ kind: episode.watched ? "unwatch-many" : "watch-many", targets });
    },
    // Marking a season sweeps every season before it, like the rows do, and
    // sends explicit episodes so the screen can be updated before Simkl
    // answers. A finished season undoes itself instead — and only itself,
    // because sweeping a correction backwards through a whole show is a lot
    // of history to lose to one tap.
    onMarkSeason: (season: SeasonView) => {
      if (simklId === null) return;
      const done = seasonState(season) === "all";
      const targets = done ? inSeason(simklId, season) : throughSeason(seasons, simklId, season);
      if (targets.length === 0) return;
      onAction({ kind: done ? "unwatch-many" : "watch-many", targets });
    },
  };
}

export function Body({
  loading,
  error,
  seasons,
  openSeasons,
  pending,
  onToggleSeason,
  actions,
}: {
  loading: boolean;
  error: boolean;
  seasons: readonly SeasonView[];
  openSeasons: ReadonlySet<number>;
  pending: boolean;
  onToggleSeason: (number: number) => void;
  actions: {
    onToggleEpisode: (episode: SeasonEpisode) => void;
    onSetPosition: (episode: SeasonEpisode) => void;
    onMarkSeason: (season: SeasonView) => void;
  };
}) {
  if (error) {
    return (
      <div className="empty">
        <strong>Could not load episodes</strong>
        <p>Close and try again.</p>
      </div>
    );
  }
  if (loading && seasons.length === 0) {
    return (
      <div className="empty">
        <strong>Loading episodes…</strong>
        <p />
      </div>
    );
  }
  if (seasons.length === 0) {
    return (
      <div className="empty">
        <strong>No episodes listed</strong>
        <p>Simkl has no episode data for this show yet.</p>
      </div>
    );
  }
  return (
    <SeasonList
      seasons={seasons}
      openSeasons={openSeasons}
      pending={pending}
      onToggleSeason={onToggleSeason}
      actions={actions}
    />
  );
}
